/**
 * Forgot Password Page
 * Allows users to request a password reset email
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader, CheckCircle } from 'lucide-react';
import { resetPasswordForEmail } from '../services/authService';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate email
    if (!email) {
      setError('Por favor ingrese su email');
      setLoading(false);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingrese un email válido');
      setLoading(false);
      return;
    }

    // Request password reset
    const { error: resetError } = await resetPasswordForEmail(email);

    if (resetError) {
      setError('Error al enviar el correo. Por favor intente nuevamente.');
      setLoading(false);
      return;
    }

    // Show success message (even if email doesn't exist - security best practice)
    setSuccess(true);
    setLoading(false);
  };

  return (
    <div className="forgot-password-container">
      {/* Background Image */}
      <div className="forgot-password-background">
        <img src="/images/paisaje-1.webp" alt="Patagonia Vital" />
        <div className="forgot-password-background-overlay"></div>
      </div>

      {/* Forgot Password Card */}
      <div className="forgot-password-card">
        {/* Logo */}
        <div className="forgot-password-logo">
          <img src="/images/Logo.png" alt="Patagonia Vital Logo" />
        </div>

        {!success ? (
          <>
            {/* Title */}
            <h1 className="forgot-password-title">¿Olvidó su contraseña?</h1>
            <p className="forgot-password-subtitle">
              Ingrese su email y le enviaremos instrucciones para restablecer su contraseña
            </p>

            {/* Error Message */}
            {error && (
              <div className="forgot-password-error">
                <span>{error}</span>
              </div>
            )}

            {/* Forgot Password Form */}
            <form onSubmit={handleSubmit} className="forgot-password-form">
              {/* Email Input */}
              <div className="forgot-password-input-group">
                <label htmlFor="email" className="forgot-password-label">
                  Email
                </label>
                <div className="forgot-password-input-wrapper">
                  <Mail className="forgot-password-input-icon" size={20} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="usuario@patagoniavital.com"
                    className="forgot-password-input"
                    disabled={loading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="forgot-password-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader className="forgot-password-button-spinner" size={20} />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    <span>Enviar Instrucciones</span>
                  </>
                )}
              </button>
            </form>

            {/* Back to Login Link */}
            <div className="forgot-password-back">
              <Link to="/login" className="forgot-password-back-link">
                <ArrowLeft size={18} />
                <span>Volver al inicio de sesión</span>
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Success Message */}
            <div className="forgot-password-success-icon">
              <CheckCircle size={64} />
            </div>
            <h1 className="forgot-password-title">Correo Enviado</h1>
            <p className="forgot-password-success-text">
              Se ha enviado un correo con instrucciones para restablecer su contraseña a{' '}
              <strong>{email}</strong>
            </p>
            <p className="forgot-password-success-subtext">
              Por favor revise su bandeja de entrada y siga las instrucciones del correo.
            </p>

            {/* Back to Login Link */}
            <div className="forgot-password-back">
              <Link to="/login" className="forgot-password-back-link">
                <ArrowLeft size={18} />
                <span>Volver al inicio de sesión</span>
              </Link>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="forgot-password-footer">
          <p>© 2025 Patagonia Vital. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
